/**
 * Upgrade modal — "Unlock the clean, watermark-free version of your continuity brief."
 *
 * Two payment flows controlled server-side via `access.payment_processor`:
 *
 *   - "lunipay" (DEFAULT): single $27 flat fee. We open the LuniPay product page
 *     in a new tab and show a trust-based "I've paid — unlock my plan" button.
 *     The /api/payments/lunipay/claim endpoint marks the assessment paid.
 *
 *   - "stripe" (MOTHBALLED): legacy per-tier Stripe Checkout flow. Reinstated
 *     by setting PAYMENT_PROCESSOR=stripe (env) or payment_processor=stripe
 *     in app_settings. No code in this file has to change — purely a runtime
 *     branch on `access.payment_processor`.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, ArrowRight, Loader2, Shield, X, ExternalLink, CheckCircle2 } from "lucide-react";
import { startCheckout, openLunipay, claimLunipay } from "@/lib/config";
import { useAuth } from "@/lib/auth";
import PayPalCheckoutButton from "@/components/PayPalCheckoutButton";

const fmtUsd = (amount) =>
  typeof amount === "number" ? `$${amount.toFixed(2).replace(/\.00$/, "")}` : "—";

export default function UpgradeModal({ open, onClose, assessmentId, access, onPaid }) {
  const [submitting, setSubmitting] = useState(false);
  const [lunipayOpened, setLunipayOpened] = useState(false);
  const [provider, setProvider] = useState("lunipay");
  const { user } = useAuth();

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Reset the 2-step state + default provider every time the modal opens.
  useEffect(() => {
    if (open) {
      setLunipayOpened(false);
      setProvider(access?.paypal_enabled ? "paypal" : "lunipay");
    }
  }, [open, access?.paypal_enabled]);

  if (!open || !access) return null;

  const processor = access.payment_processor || "lunipay";
  const isLunipay = processor === "lunipay";
  const paypalEnabled = !!access.paypal_enabled;
  // Trust-based LuniPay self-unlock is controlled server-side; when disabled we
  // never render the "I've paid" claim flow.
  const lunipayClaim = !!access.lunipay_claim_enabled;
  const processorName = access.processor_name || "Payment Processor";
  // The provider picker is shown only when PayPal is available alongside a
  // usable LuniPay claim flow. When only one is available we render that single
  // flow with no picker.
  const showPicker = paypalEnabled && isLunipay && lunipayClaim;

  const price = isLunipay
    ? access.flat_price_usd
    : access.brief_price_usd;
  const label = isLunipay
    ? (access.flat_label || "Biz Resilience Scan")
    : (access.brief_label || "Continuity Brief");
  const modePretty = ({
    solopreneur: "Solopreneur",
    sme: "Small / Medium Enterprise",
    corporate: "Corporate Department",
  })[access.mode] || access.mode;

  // ---- Stripe (mothballed) path ----
  const goPayStripe = async () => {
    try {
      setSubmitting(true);
      await startCheckout({ assessment_id: assessmentId, tier: "brief" });
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Could not start checkout. Please try again.");
      setSubmitting(false);
    }
  };

  // ---- LuniPay (live) path ----
  const goPayLunipay = () => {
    const popup = openLunipay(access.lunipay_url);
    if (!popup) {
      toast.error("Your browser blocked the popup. Please allow popups for this site, then try again.");
      return;
    }
    setLunipayOpened(true);
    toast.message(`${processorName} opened in a new tab. Come back here after you've paid.`);
  };

  const claimLunipayUnlock = async () => {
    try {
      setSubmitting(true);
      const res = await claimLunipay({ assessment_id: assessmentId });
      if (res.already_paid) {
        toast.message("This brief was already unlocked. Refreshing…");
      } else {
        toast.success("Unlocked! Loading your clean brief…");
      }
      onPaid?.(res);
      onClose?.();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Could not unlock. Please try again or contact support.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="upgrade-modal"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg rounded-xl bg-white dark:bg-slate-900 shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-1.5 bg-gradient-to-r from-teal-500 via-teal-600 to-emerald-600" />
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 inline-flex items-center justify-center h-8 w-8 rounded-full text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="p-6 sm:p-8">
          <p className="text-[11px] uppercase tracking-[0.18em] text-teal-700 dark:text-teal-400">Upgrade</p>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold text-slate-900 dark:text-slate-50 tracking-tight">
            Unlock your full Biz Resilience Scan
          </h2>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
            Your scan is ready. Right now it's marked <span className="font-semibold">DRAFT</span> on the PDF and share view. Unlock once for a clean, watermark-free version you can download, share, and use with your team.
          </p>

          <div className="mt-6 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/40 p-5">
            <div className="flex items-baseline justify-between gap-3">
              <div>
                <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">{modePretty}</p>
                <p className="mt-1 font-display text-base font-semibold text-slate-900 dark:text-slate-50">{label}</p>
              </div>
              <p className="font-display text-3xl font-semibold text-slate-900 dark:text-slate-50">
                {fmtUsd(price)}
                <span className="text-xs text-slate-500 dark:text-slate-400 font-normal ml-1">USD</span>
              </p>
            </div>
            <ul className="mt-5 space-y-2 text-sm text-slate-700 dark:text-slate-200">
              <li className="flex items-start gap-2"><span className="mt-1 h-1.5 w-1.5 rounded-full bg-teal-600 shrink-0" /> Clean, watermark-free PDF export</li>
              <li className="flex items-start gap-2"><span className="mt-1 h-1.5 w-1.5 rounded-full bg-teal-600 shrink-0" /> Branded logo on the cover page</li>
              <li className="flex items-start gap-2"><span className="mt-1 h-1.5 w-1.5 rounded-full bg-teal-600 shrink-0" /> 30-day shareable link without the draft notice</li>
              <li className="flex items-start gap-2"><span className="mt-1 h-1.5 w-1.5 rounded-full bg-teal-600 shrink-0" /> Saved to your account for re-download anytime</li>
            </ul>
          </div>

          {!user && (
            <div className="mt-4 rounded-md border border-amber-200 dark:border-amber-900/60 bg-amber-50/70 dark:bg-amber-900/20 p-3 text-xs text-amber-900 dark:text-amber-200">
              You're not signed in. You can still pay now — we'll attach this scan to your account when you sign in afterwards.
            </div>
          )}

          {/* --- Provider picker (PayPal vs LuniPay) --- */}
          {showPicker && (
            <div className="mt-6" data-testid="provider-picker">
              <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400 mb-2">Choose how to pay</p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setProvider("paypal")}
                  data-testid="provider-pick-paypal"
                  className={`h-11 rounded-md border text-sm font-medium transition-colors ${
                    provider === "paypal"
                      ? "border-teal-600 bg-teal-50 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200"
                      : "border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                  }`}
                >
                  PayPal / Card
                </button>
                <button
                  type="button"
                  onClick={() => setProvider("lunipay")}
                  data-testid="provider-pick-lunipay"
                  className={`h-11 rounded-md border text-sm font-medium transition-colors ${
                    provider === "lunipay"
                      ? "border-teal-600 bg-teal-50 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200"
                      : "border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                  }`}
                >
                  {processorName}
                </button>
              </div>
            </div>
          )}

          {/* --- Action area --- */}
          {(provider === "paypal" && paypalEnabled) ? (
            <div className="mt-5 space-y-3">
              <PayPalCheckoutButton
                clientId={access.paypal_client_id}
                productSlug="brief"
                resourceRef={assessmentId}
                onPaid={(res) => { onPaid?.(res); onClose?.(); }}
              />
              <div className="flex items-center justify-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                <Shield className="h-3 w-3" /> Secure payment via PayPal — pay with PayPal or any card
              </div>
            </div>
          ) : (isLunipay && lunipayClaim) ? (
            <>
              {!lunipayOpened ? (
                <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                    <Shield className="h-3 w-3" />
                    Secure payment via {processorName}
                  </div>
                  <button
                    onClick={goPayLunipay}
                    data-testid="upgrade-lunipay-btn"
                    className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 transition-colors"
                  >
                    <Lock className="h-4 w-4" />
                    {`Pay ${fmtUsd(price)} on ${processorName}`}
                    <ExternalLink className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="mt-6 space-y-4">
                  <div className="rounded-md border border-teal-200 dark:border-teal-900/60 bg-teal-50/80 dark:bg-teal-900/20 p-4 text-sm text-teal-900 dark:text-teal-100">
                    <p className="font-semibold flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" /> {processorName} opened in a new tab.
                    </p>
                    <p className="mt-1.5 text-[13px] leading-relaxed">
                      Complete your {fmtUsd(price)} payment there. Once you see the confirmation, come back here and click the button below — your clean scan unlocks instantly.
                    </p>
                  </div>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <button
                      onClick={() => openLunipay(access.lunipay_url)}
                      data-testid="upgrade-lunipay-reopen-btn"
                      type="button"
                      className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-xs hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                    >
                      <ExternalLink className="h-3 w-3" /> Reopen {processorName}
                    </button>
                    <button
                      onClick={claimLunipayUnlock}
                      disabled={submitting}
                      data-testid="upgrade-lunipay-claim-btn"
                      className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-500 transition-colors disabled:opacity-50"
                    >
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                      {submitting ? "Unlocking…" : "I've paid — unlock my plan"}
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                <Shield className="h-3 w-3" />
                Secure checkout via Stripe
              </div>
              <button
                onClick={goPayStripe}
                disabled={submitting}
                data-testid="upgrade-checkout-btn"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 transition-colors disabled:opacity-50"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                {submitting ? "Redirecting…" : `Unlock for ${fmtUsd(price)}`}
                {!submitting && <ArrowRight className="h-4 w-4" />}
              </button>
            </div>
          )}

          <p className="mt-4 text-[11px] text-slate-500 dark:text-slate-400 text-center">
            One-time payment · No subscription · Refundable within 7 days if you're not satisfied
          </p>
        </div>
      </div>
    </div>
  );
}
